// hooks/useResponsiveBreakpoints.js
import { useState, useEffect, useCallback, useRef } from 'react';

const BREAKPOINTS = {
  mobile: 768,
  tablet: 992,
  desktop: 1200,
  large: 1400
};

const DEBOUNCE_DELAY = 150;

/**
 * Custom hook for responsive breakpoints with optimized performance
 * - Single global resize listener
 * - Debounced updates
 * - Memoized breakpoint calculations
 */
export const useResponsiveBreakpoints = () => {
  const [dimensions, setDimensions] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800
  }));

  const timeoutRef = useRef(null);
  const frameRef = useRef(null);

  const updateDimensions = useCallback(() => {
    // Use requestAnimationFrame for smooth updates
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
    }
    
    frameRef.current = requestAnimationFrame(() => {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;
      
      // Only update if dimensions actually changed
      setDimensions(prev => {
        if (prev.width !== newWidth || prev.height !== newHeight) {
          return { width: newWidth, height: newHeight };
        }
        return prev;
      });
    });
  }, []);

  const debouncedUpdateDimensions = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(updateDimensions, DEBOUNCE_DELAY);
  }, [updateDimensions]);

  useEffect(() => {
    // Passive listener for better performance
    window.addEventListener('resize', debouncedUpdateDimensions, { passive: true });
    
    // Initial call
    updateDimensions();
    
    return () => {
      window.removeEventListener('resize', debouncedUpdateDimensions);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [debouncedUpdateDimensions, updateDimensions]);

  // Memoized breakpoint calculations
  const breakpoints = {
    isMobile: dimensions.width <= BREAKPOINTS.mobile,
    isTablet: dimensions.width > BREAKPOINTS.mobile && dimensions.width <= BREAKPOINTS.tablet,
    isDesktop: dimensions.width > BREAKPOINTS.tablet && dimensions.width <= BREAKPOINTS.desktop,
    isLarge: dimensions.width > BREAKPOINTS.desktop,
    width: dimensions.width,
    height: dimensions.height,
    
    // Utility functions
    isAtLeast: (breakpoint) => dimensions.width >= BREAKPOINTS[breakpoint],
    isAtMost: (breakpoint) => dimensions.width <= BREAKPOINTS[breakpoint]
  };

  return breakpoints;
};